import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import * as LucideIcons from 'lucide-react';
import { ArrowRight, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Services = () => {
  const [services, setServices] = useState([]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error('Error fetching services:', error);
      }
    };
    fetchServices();
  }, []);

  const additionalBenefits = [
    'Free initial consultation',
    'Secure document upload portal',
    'Electronic filing for faster refunds',
    'Audit protection and support',
    'Year-round tax advice',
    'Multi-state tax expertise'
  ];

  return (
    <div className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="py-20 px-6 lg:px-8 bg-gradient-to-br from-teal-50 to-white">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6 animate-in fade-in slide-in-from-top duration-700">
            Our Tax Services
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto animate-in fade-in slide-in-from-top duration-700 delay-100">
            Comprehensive tax solutions designed to maximize your refund, minimize your liability, and provide peace of mind.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const IconComponent = LucideIcons[service.icon];
              return (
                <Card key={service.id} className={`border-2 border-gray-100 hover:border-teal-300 transition-all hover:shadow-xl group animate-in fade-in slide-in-from-bottom duration-700 delay-${index * 100}`}>
                  <CardContent className="p-8 space-y-4">
                    <div className="h-16 w-16 bg-teal-50 rounded-2xl flex items-center justify-center group-hover:bg-teal-100 transition-colors">
                      {IconComponent && <IconComponent className="h-8 w-8 text-teal-600" />}
                    </div>
                    <h3 className="text-2xl font-semibold text-gray-900">{service.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{service.description}</p>
                    <Link to="/contact">
                      <Button variant="ghost" className="text-teal-600 hover:text-teal-700 hover:bg-teal-50 p-0 h-auto font-semibold">
                        Learn More
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">What's Included</h2>
              <p className="text-lg text-gray-600 mb-8">
                Every service package includes comprehensive support and additional benefits to ensure your complete satisfaction.
              </p>
              <div className="grid gap-4">
                {additionalBenefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="h-6 w-6 text-teal-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&h=800&fit=crop"
                  alt="Tax Services"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-6">
                <div className="text-3xl font-bold text-teal-600">$0</div>
                <div className="text-sm text-gray-600">Consultation Fee</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Simple Process</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Getting started is easy. We've streamlined our process to make tax preparation as smooth as possible.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Book Appointment', description: 'Choose your preferred tax preparer and schedule a convenient time.' },
              { step: '02', title: 'Gather Documents', description: 'We will provide a checklist of documents needed for your tax situation.' },
              { step: '03', title: 'Meet & Review', description: 'Meet with your preparer to review your documents and discuss your situation.' },
              { step: '04', title: 'File & Relax', description: 'We handle the filing while you relax, knowing your taxes are in expert hands.' }
            ].map((item, index) => (
              <div key={index} className="relative">
                <div className="text-6xl font-bold text-teal-100 mb-4">{item.step}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
                {index < 3 && (
                  <div className="hidden md:block absolute top-12 left-full w-full h-0.5 bg-teal-200 -z-10" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 lg:px-8 bg-gradient-to-br from-teal-600 to-teal-700 text-white">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-4xl lg:text-5xl font-bold">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-teal-50">
            Book your free consultation today and discover the Takeover Tax difference.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-teal-600 hover:bg-gray-100 text-lg px-8 py-6 transition-all hover:scale-105 hover:shadow-xl">
              Schedule Your Consultation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
